import { type ColumnDef, createColumnHelper, type RowSelectionState } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { CircleSlash, Copy, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { TenantRow } from "~/features/admin/tenants/types";

interface TenantsTableProps {
  rows: TenantRow[];
  onSelect: (id: string) => void;
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
}

const helper = createColumnHelper<DataTableFeatures, TenantRow>();

export function TenantsTable({
  rows,
  onSelect,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  rowSelection,
  onRowSelectionChange,
}: TenantsTableProps) {
  const { t } = useTranslation();

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, TenantRow, any>[] = [
    helper.display({
      id: "select",
      enableSorting: false,
      enableGlobalFilter: false,
      // Header checkbox toggles the *filtered* rows so that "select all" and a
      // narrowed search compose (絞り込み + 一括選択). Rows selected under a
      // different filter are preserved, so cohorts from multiple searches can
      // be combined into one bulk install.
      header: (info) => {
        const filteredRows = info.table.getFilteredRowModel().rows;
        const allSelected = filteredRows.length > 0 && filteredRows.every((row) => row.getIsSelected());
        const someSelected = filteredRows.some((row) => row.getIsSelected());
        return (
          <Checkbox
            checked={allSelected ? true : someSelected ? "indeterminate" : false}
            onCheckedChange={(next) => {
              const selection: RowSelectionState = { ...rowSelection };
              for (const row of filteredRows) {
                if (next === true) {
                  selection[row.id] = true;
                } else {
                  delete selection[row.id];
                }
              }
              onRowSelectionChange(selection);
            }}
            aria-label={t("ui.selectAll")}
          />
        );
      },
      cell: (info) => (
        <Checkbox
          checked={info.row.getIsSelected()}
          onCheckedChange={() => info.row.toggleSelected()}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("ui.select")}
        />
      ),
    }),
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleSlash className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("name", {
      header: () => t("model.common.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("shortCode", {
      header: () => t("model.tenant.shortCode"),
      cell: (info) => <span className="font-mono text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("organization.name", {
      id: "organization",
      header: () => t("model.organization.modelName"),
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("ownerLabel", {
      header: () => t("model.tenant.owner"),
      cell: (info) => {
        const label = info.getValue();
        if (!label) return <span className="text-fg-3">{t("model.tenant.noOwner")}</span>;
        return <span className="text-fg-2">{label}</span>;
      },
    }),
    helper.accessor("updatedAt", {
      header: () => t("model.common.updatedAt"),
      enableGlobalFilter: false,
      cell: (info) => (
        <span className="tabular-nums text-fg-2">{t("date.long", { date: parseISO(info.getValue()) })}</span>
      ),
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  return (
    <DataTable<TenantRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      getRowId={(row) => row.id}
    />
  );
}
